import { Component, ViewChild, OnInit } from '@angular/core';
import { AlertService } from '@app/shared/components/organisms/alert/alert.service';
import { Color } from '@app/shared/models/color.model';
import { chunkFiles } from '@app/shared/utils/utils';
import { EditedInvoiceService } from './edited-invoice.service';
import { forkJoin } from 'rxjs';
import { EditedInvoicesStatusEnum } from '@app/shared/models/edited-invoices.model';
import { Size, Style } from '@app/shared/components/atoms/icon/icon.component';
import { ButtonIcon, ButtonStyle } from '@app/shared/components/atoms/buttons/button.type';
import { SelectionModel } from '@angular/cdk/collections';
import { ConfirmDialogData } from '@app/shared/components/organisms/dialog/dialog.data';
import { DialogService } from '@app/shared/components/organisms/dialog/dialog.service';
import { MESSAGES } from '@app/shared/utils/messages';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { EditedInvoice } from '@app/shared/interfaces/edited-invoice.inteface';

@Component({
  selector: 'app-edited-invoice',
  templateUrl: './edited-invoice.component.html',
  styleUrl: './edited-invoice.component.scss',
  standalone: false,
})
export class EditedInvoiceComponent implements OnInit {
  protected readonly Color = Color;

  protected readonly Object = Object;

  isImporting: boolean = false;

  protected readonly EditedInvoicesStatusEnum = EditedInvoicesStatusEnum;

  @ViewChild(BaseTableComponent, { static: false })
  private baseTable!: BaseTableComponent<EditedInvoice>;

  protected showImport: boolean = true;

  protected inputValue: string = '';

  protected isLoading: boolean = true;

  protected downloadLoaders: boolean[] = [];

  protected readonly Style = Style;
  protected readonly ButtonIcon = ButtonIcon;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly IconSize = Size;

  private editedInvoiceSource: EditedInvoice[] = [];

  protected editedInvoicesSourceSearch: EditedInvoice[] = [];

  protected risSelection = new SelectionModel<string>(true, []);

  constructor(
    private alertService: AlertService,
    private editedInvoiceService: EditedInvoiceService,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.getInvoice();
  }

  getInvoice() {
    this.isLoading = true;
    this.editedInvoiceService.getEditedInvoices().subscribe({
      next: (response) => {
        this.editedInvoicesSourceSearch = response.Items;
        this.editedInvoiceSource = response.Items;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur lors de la récupération des factures.', error);
      },
    });
  }

  async importFile(files: FileList) {
    if (!files.length) return;
    this.isImporting = true;
    // convert files to base64 string
    const filesArray = Array.from(files);

    const base64Files: string[] = await Promise.all(
      filesArray.map(async (file) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        return new Promise((resolve) => {
          fileReader.onload = () => {
            const base64String = fileReader.result as string;
            resolve(`filename=${file.name},content=${base64String}`);
          };
        });
      }),
    );

    const chunks = chunkFiles(base64Files);

    forkJoin(chunks.map((chunk) => this.editedInvoiceService.importEditedInvoices(chunk))).subscribe({
      error: () => {
        this.alertService.error("Une ou plusieurs factures n'ont pas pu être importées.");
      },
      next: () => {
        this.alertService.success('Facture(s) importée(s) avec succès.');
      },
    });

    this.isImporting = false;
  }

  editedInvoiceStatusChange(status: string) {
    this.showImport = EditedInvoicesStatusEnum.IMPORT === status;
  }

  getEditedInvoicesSearch(searchInput: string) {
    this.isLoading = true;

    if (searchInput === '') {
      this.editedInvoicesSourceSearch = this.editedInvoiceSource;
    } else {
      this.editedInvoicesSourceSearch = this.editedInvoiceSource.filter((item) =>
        item.documentId.toLowerCase().includes(searchInput.toLowerCase()),
      );
    }
    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  /**
   * Permet de selectionner l'ensemble des factures editées
   * @param select Si le bouton est selectionné
   */
  checkChange(select: boolean) {
    const editedInvoiceIds = this.baseTable.displayableRows.map(({ timestamp }) => timestamp);

    if (!select) {
      this.risSelection.deselect(...editedInvoiceIds);
    } else {
      this.risSelection.select(...editedInvoiceIds);
    }
  }

  /**
   * Label sur la checkbox
   * @param selectionId l'id de l'élément
   */
  checkboxLabel(selectionId?: string): string {
    if (!selectionId) {
      return `${this.isAllPageSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.risSelection.isSelected(selectionId) ? 'deselect' : 'select'} row ${selectionId + 1}`;
  }

  isAllPageSelected() {
    const numSelected = this.editedInvoicesSourceSearch.filter(({ timestamp }) =>
      this.risSelection.selected.find((s) => s === timestamp),
    );
    const numRows = this.editedInvoicesSourceSearch.length;
    return numSelected.length === numRows;
  }

  isCurrentPageSelected() {
    const numSelected = this.editedInvoicesSourceSearch.filter(({ timestamp }) =>
      this.risSelection.selected.find((s) => s === timestamp),
    );
    const numRows = this.editedInvoicesSourceSearch.length;
    return numSelected.length > 0 && numSelected.length !== numRows;
  }

  showCommentDialog(editedInvoice: EditedInvoice) {
    const options: ConfirmDialogData = {
      textArea: editedInvoice.comment ? editedInvoice.comment : '',
      title: 'Ajouter un commentaire',
      confirmText: 'Enregistrer',
      placeholder: 'Veuillez saisir votre commentaire',
      cancelText: 'Annuler',
      enableConfirmButton: true,
      width: '50rem',
      readOnly: false,
      confirmFunction: (value) => {
        const updateEditedInvoice: Partial<EditedInvoice> = {
          documentId: editedInvoice.documentId,
          comment: value,
        };
        this.editedInvoiceService.putEditedInvoices(updateEditedInvoice).subscribe({
          next: () => {
            this.editedInvoicesSourceSearch = this.editedInvoicesSourceSearch.map((inv) =>
              inv.timestamp === editedInvoice.timestamp ? { ...inv, comment: value } : inv,
            );
          },
          error: () => {},
        });
      },
    };
    this.dialogService.open(options);
  }

  downloadEditedInvoice(event: Event, editedInvoice: EditedInvoice, index: number) {
    this.downloadLoaders[index] = true;

    this.editedInvoiceService.downloadEditedInvoice(this.editedInvoicesSourceSearch[index].documentId).subscribe({
      next: (base64String: string) => {
        const blob = new Blob([new Uint8Array([...atob(base64String)].map((c) => c.charCodeAt(0)))], {
          type: 'application/pdf',
        });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${this.editedInvoicesSourceSearch[index].documentId}.pdf`;
        a.click();
        this.downloadLoaders[index] = false;
      },
      error: () => {
        this.alertService.error(MESSAGES.DOWNLOAD_ERROR);
        this.downloadLoaders[index] = false;
      },
    });
  }

  deleteEditedInvoices() {
    this.isLoading = true;
    const deleteList = this.risSelection.selected.map((timestamp) => {
      const matchedInvoice = this.editedInvoicesSourceSearch.find((invoice) => invoice.timestamp === timestamp);
      return matchedInvoice ? matchedInvoice : { timestamp };
    });
    this.editedInvoiceService.deleteEditedInvoices(deleteList).subscribe({
      next: () => {
        this.alertService.success('Facture(s) supprimée(s) avec succès.');
        this.refreshInvoices();
        this.risSelection.clear();
        this.isLoading = true;
      },
      error: (err) => {
        console.log(err);
        this.alertService.error(MESSAGES.DELETE_ERROR);
        this.isLoading = true;
      },
    });
  }

  private refreshInvoices() {
    this.editedInvoiceSource = [];
    this.editedInvoicesSourceSearch = [];
    this.getInvoice();
  }
}
